import { useInstantSearch } from "react-instantsearch";
import { Search as SearchIcon, Loader2 } from "lucide-react";

const ICON_CLASS = "w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none";

/**
 * Shared loading UI for every search tab. The plain components take an
 * explicit `loading` flag (UnifiedSearch's own fetch state); the
 * `InstantSearch*` variants read react-instantsearch's request status, so
 * they must be rendered inside an <InstantSearch> tree.
 */

export function SearchInputIcon({ loading }) {
  return loading ? (
    <Loader2 className={`${ICON_CLASS} text-red-500 animate-spin`} data-testid="search-loading-icon" />
  ) : (
    <SearchIcon className={`${ICON_CLASS} text-neutral-500`} />
  );
}

export function LoadingRow({ label = "Suche läuft…" }) {
  return (
    <div
      className="flex items-center justify-center gap-2 py-10 text-sm text-neutral-400 border border-neutral-800/70 rounded-xl bg-panel"
      role="status"
      aria-live="polite"
      data-testid="search-loading"
    >
      <Loader2 className="w-5 h-5 animate-spin text-red-500" />
      {label}
    </div>
  );
}

// Before the first response there is nothing to show, so the spinner takes
// the results' place; on later searches the previous results stay visible
// (dimmed) so the layout doesn't jump on every keystroke.
export function ResultsLoading({ loading, hasResults, children }) {
  if (loading && !hasResults) return <LoadingRow />;
  return (
    <div
      className={loading ? "opacity-50 pointer-events-none transition-opacity" : "transition-opacity"}
      aria-busy={loading}
    >
      {children}
    </div>
  );
}

function useInstantSearchLoading() {
  const { status, results } = useInstantSearch();
  return {
    loading: status === "loading" || status === "stalled",
    hasResults: Boolean(results) && !results.__isArtificial,
  };
}

export function InstantSearchInputIcon() {
  const { loading } = useInstantSearchLoading();
  return <SearchInputIcon loading={loading} />;
}

export function InstantSearchResultsLoading({ children }) {
  const { loading, hasResults } = useInstantSearchLoading();
  return (
    <ResultsLoading loading={loading} hasResults={hasResults}>
      {children}
    </ResultsLoading>
  );
}
